import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import Group from 'components/utils/Group'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import FormatLoyaltyPoints from 'loyalty/components/Formatters/FormatLoyaltyPoints'
import FormatLoyaltyStatusCredits from 'loyalty/components/Formatters/FormatLoyaltyStatusCredits'
import { LOYALTY_LUX_PLUS_RENEWAL_BONUS_POINTS, LOYALTY_LUX_PLUS_RENEWAL_BONUS_STATUS_CREDITS } from 'loyalty/constants/loyaltyBonusPoints'
import { LUXURY_PLUS } from 'luxPlus/constants/base'
import { useCallback } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import LuxPlusManageAccountModalMembershipStatusBanner from './LuxPlusManageAccountModalMembershipStatusBanner'
import LuxPlusManageAccountModalSavingsBanner from './LuxPlusManageAccountModalSavingsBanner'
import { useAppSelector } from 'hooks/reduxHooks'
import getIsLoyaltyLuxPlusRenewalBonusEnabled from 'loyalty/selectors/getIsLoyaltyLuxPlusRenewalBonusEnabled'

interface Props {
  item: App.MemberSubscriptionItem
  onBack: () => void
}

function LuxPlusManageAccountModalRenewalCancellationSuccess({
  item,
  onBack,
}: Props) {
  const intl = useIntl()
  const modalElement = useModalElementContext()
  const dismissModal = useCallback(() => {
    modalElement.resolve()
  }, [modalElement])
  const isLoyaltyLuxPlusRenewalBonusEnabled = useAppSelector(getIsLoyaltyLuxPlusRenewalBonusEnabled)

  return <>
    <ModalHeader
      title={intl.formatMessage({
        id: 'RnYNBPhhaphcroO3ccSK',
        defaultMessage: 'Manage membership',
        description: 'Title for the manage membership modal after cancellation success',
      })}
      onBackButtonClick={onBack}
    />
    <ModalBody>
      <ModalContent>
        <Group direction="vertical" gap={24}>
          <Group direction="vertical" gap={12}>
            <Heading variant="heading4">
              {intl.formatMessage(
                {
                  id: 'dpfH6ZPGu_AEV2d8dCgj',
                  defaultMessage: 'Confirming your {programName} auto renewal is now cancelled and thank you for your membership.',
                  description: 'Heading confirming the user’s membership auto-renewal has been cancelled',
                },
                { programName: LUXURY_PLUS.PROGRAM_NAME },
              )}
            </Heading>
            <BodyText variant="large">
              {isLoyaltyLuxPlusRenewalBonusEnabled && <FormattedMessage
                id="LCQSbhg3c6nsiytfQrge"
                defaultMessage="Remember you have up to the end of your current membership year to change your mind, renew your membership and receive your <bold>bonus {loyaltyPoints} and {statusCredits}</bold>."
                description="Reminder after cancelling LuxPlus+ renewal, encouraging re-activation with loyalty bonus rewards"
                values={{
                  loyaltyPoints: <FormatLoyaltyPoints points={LOYALTY_LUX_PLUS_RENEWAL_BONUS_POINTS} />,
                  statusCredits: <FormatLoyaltyStatusCredits statusCredits={LOYALTY_LUX_PLUS_RENEWAL_BONUS_STATUS_CREDITS} />,
                  bold: (chunks) => <b>{chunks}</b>,
                }}
              />}
              {!isLoyaltyLuxPlusRenewalBonusEnabled && <FormattedMessage
                id="X6KhYCp1A78XSMI5kmbM"
                defaultMessage="Remember you have up to the end of your current membership year to change your mind, renew your membership and <bold>enjoy your member only benefits.</bold>"
                description="Reminder after cancelling LuxPlus+ renewal, encouraging re-activation to keep member benefits"
                values={{
                  bold: (chunks) => <b>{chunks}</b>,
                }}
              />}
            </BodyText>
          </Group>
          <Group direction="vertical" gap={8} noDisplayWhenEmpty>
            <LuxPlusManageAccountModalSavingsBanner />
            <LuxPlusManageAccountModalMembershipStatusBanner item={item} />
          </Group>
        </Group>
      </ModalContent>
    </ModalBody>
    <ModalFooter primaryActionProps={{
      children: intl.formatMessage({
        id: 'g339uFrIDUiMOPLfFc3S',
        defaultMessage: 'All done',
        description: 'Button text to dismiss the cancellation success modal',
      }),
      onClick: dismissModal,
    }}
    />
  </>
}

export default LuxPlusManageAccountModalRenewalCancellationSuccess
